import React, { useMemo } from "react";

import { useTranslation } from "utils";

import { Flex, Icon, Typography } from "components";

import ResilienceScoreDonutChart from "pages/app/resilience-score/components/resilience-score-context-drawer/ResilienceScoreDonutChart";
import { formatScore } from "pages/app/resilience-score/utils/ResilienceScoreContextDrawerUtils";
import { ResilienceScoreViewModel, calcPct } from "pages/app/resilience-score/utils/ResilienceScoreUtils";

type ResilienceScoreSummaryCardProps = {
  selectedYear: number;
  current: ResilienceScoreViewModel | null;
  previous: ResilienceScoreViewModel | null;
};

const formatChangePct = (pct: number | null): string => {
  if (pct == null || Number.isNaN(pct)) return "0%";
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
};

const ResilienceScoreSummaryCard = ({ selectedYear, current, previous }: ResilienceScoreSummaryCardProps) => {
  const { t } = useTranslation(["resilienceScore"]);
  const changePct = useMemo(() => {
    if (!current || !previous) return null;

    return calcPct(current.overallScore - previous.overallScore, previous.overallScore);
  }, [current, previous]);

  const score = current?.overallScore ?? 0;
  const max = current?.maxScore ?? 10;

  let message = null;
  let iconName = null;

  if (changePct == null || Number.isNaN(changePct)) {
    message = t(
      "resilienceScoreContextDrawer.summary.firstYearMessage",
      "This is the first year your score can be calculated. Add your plan for the coming years to see how it develops."
    );
    iconName = "tabler/arrow-straight-right-circle";
  } else if (changePct > 0) {
    message = t("resilienceScoreContextDrawer.summary.improvedMessage", "Right on! Your farm resilience score looks good.");
    iconName = "tabler/arrow-up-right-circle";
  } else if (changePct < 0) {
    message = t("resilienceScoreContextDrawer.summary.declinedMessage", "Our resilience score has decrease since last year.");
    iconName = "tabler/arrow-down-right-circle";
  } else {
    message = t("resilienceScoreContextDrawer.summary.flatMessage", "Keep it rolling! There might be more interventions with positive impact possible.");
    iconName = "tabler/arrow-straight-right-circle";
  }

  const formattedScore = formatScore(score, "0");

  return (
    <Flex vertical className="rsd-card rsd-summary-card">
      <Flex className="rsd-summary-layout">
        <Flex align="center" className="rsd-summary-left" gap="0.75rem">
          <ResilienceScoreDonutChart progressPct={score} />
          <Flex align="baseline" gap="0.25rem">
            <Typography variant="h1" className="rsd-summary-amount hero">
              {formattedScore}
            </Typography>
            <Typography variant="paragraph-xl" className="rsd-summary-score-divider">
              /{max}
            </Typography>
          </Flex>
        </Flex>
        <div className="rsd-summary-divider" />
        <Flex vertical className="rsd-summary-copy" gap="0.5rem">
          {changePct !== null && changePct !== undefined && (
            <Flex className="rsd-summary-status" gap="0.25rem">
              <Icon iconName={iconName} size="s" />
              <Typography variant="paragraph-s" className="rsd-summary-status-text">
                {t("resilienceScoreContextDrawer.summary.changeVsYear", {
                  change: formatChangePct(changePct),
                  year: selectedYear - 1,
                })}
              </Typography>
            </Flex>
          )}
          <Typography variant="paragraph-s" className="rsd-summary-text">
            {message}
          </Typography>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default ResilienceScoreSummaryCard;
